import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { map } from "rxjs/operators";
import { SubdomainService } from "src/app/core/_base/subdomain.service";
import { MadAuthService } from "src/app/core/mad-auth/mad-auth.service";
import { environment } from "src/environments/environment";

export type StoreValuationReportTemplatePayload = {
  name: string,
  client_id: number,
  cover_page_included: boolean,
  toc_included: boolean,
  back_page_included: boolean,
  sections: Array<{
    name: string,
    included: boolean,
    chapters: Array<{
      name: string,
      included: boolean
    }>
  }>
}

type ValuationReportTemplateListItemAPI = {
  id: number
  name: string,
  reporting_processes_count: number
}

@Injectable({
  providedIn: 'root'
})
export class ValuationReportTemplateService {
  constructor(
    private http: HttpClient,
    private authService: MadAuthService,
    private subdomainService: SubdomainService
  ) {}

  fetchList() {
    return this.http.get<{
      data: Array<ValuationReportTemplateListItemAPI>,
      total: number,
      current_page: number,
      per_page: number
    }>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/valuation`,
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  fetch(id: number) {
    return this.http.get<{
      data: {
        id: number,
        cover_page_included: number,
        toc_included: number,
        back_page_included: number,
        name: string,
        client_id: number,
        sections: Array<{
          id: number,
          included: number,
          disabled: number,
          name: string,
          default_name: string,
          chapters: Array<{
            id: number
            included: number
            name: string,
            default_name: string
          }>
        }>
      }
    }>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/valuation/${id}`,
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  create(payload: StoreValuationReportTemplatePayload) {
    return this.http.post<{
      data: {
        id: number,
        name: string
      }
    }>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/valuation`,
      payload,
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  update(id: number, payload: StoreValuationReportTemplatePayload) {
    return this.http.put<unknown>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/valuation/${id}`,
      payload,
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  fetchTrashedList() {
    return this.http.get<{data: {
      id: number,
      name: string,
      deleted_by: string,
      deleted_at: string
    }[]}>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/valuation/trashed`,
      {
        headers: this.authService.getAuthHeaders()
      }
    ).pipe(
      map(response => response.data)
    )
  }

  fetchAdminTrashedList() {
    return this.http.get<{data: {
      id: number,
      name: string,
      deleted_by: string,
      deleted_at: string
    }[]}>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/valuation/admin-trashed`,
      {
        headers: this.authService.getAuthHeaders()
      }
    ).pipe(
      map(response => response.data)
    )
  }

  delete(id: number) {
    return this.http.delete<void>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/valuation/${id}`,
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  deleteFromTrash(id: number) {
    return this.http.delete<void>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/valuation/trashed/${id}`,
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  deleteFromAdminTrash(id: number) {
    return this.http.delete<void>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/valuation/admin-trashed/${id}`,
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  flushTrash() {
    return this.http.delete<void>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/valuation/flush`,
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  restoreFromTrash(id: number) {
    return this.http.post<void>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/valuation/restore/${id}`,
      {},
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  restoreFromAdminTrash(id: number) {
    return this.http.post<void>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/valuation/admin-restore/${id}`,
      {},
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  duplicate(id: number) {
    return this.http.post<void>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/valuation/duplicate/${id}`,
      {},
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }
}